import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserCheck, Plus, Search, Edit2, Trash2,
  Mail, Calendar, AlertCircle, Users
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserPartner } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Toast } from '../../../components/Toast';

interface Coach {
  id: string;
  userId: string;
  name: string;
  email: string;
  tier: string;
  respondentLimit: number;
  teamsAccessEnabled: boolean;
  crmEnabled: boolean;
  aiAnalysisAccess: boolean;
  createdAt: Date;
}

export function PartnerCoachManagementTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [coachLimit, setCoachLimit] = useState(10);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    tier: 'basic',
    respondentLimit: 25
  });
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/tr/login');
          return;
        }

        const { data: partner } = await supabase
          .from('partners')
          .select('id, max_coaches')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!partner) {
          navigate('/tr/login');
          return;
        }

        setPartnerId(partner.id);
        setCoachLimit(partner.max_coaches || 10);
        await loadCoaches(partner.id);
      } catch (err) {
        console.error('Error checking partner status:', err);
        navigate('/tr/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadCoaches = async (pId: string) => {
    try {
      const { data: coachesData } = await supabase
        .from('coaches')
        .select('id, user_id, tier, max_respondents, teams_access_enabled, crm_enabled, ai_analysis_access, created_at, users!coaches_user_id_fkey(full_name, email)')
        .eq('partner_id', pId);

      const mapped = (coachesData || []).map(c => ({
        id: c.id,
        userId: c.user_id,
        name: (c.users as any)?.full_name || (c.users as any)?.email || 'Bilinmiyor',
        email: (c.users as any)?.email || '',
        tier: c.tier || 'basic',
        respondentLimit: c.max_respondents || 25,
        teamsAccessEnabled: c.teams_access_enabled || false,
        crmEnabled: c.crm_enabled || false,
        aiAnalysisAccess: c.ai_analysis_access || false,
        createdAt: new Date(c.created_at || Date.now())
      })) as Coach[];

      setCoaches(mapped);
    } catch (err) {
      console.error('Error loading coaches:', err);
      setError('Koclar yuklenemedi');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setModalError(null);

    if (!partnerId || isSaving) return;

    if (!editingCoach && coaches.length >= coachLimit) {
      setModalError(`Koc limitinize ulastiniz (${coachLimit})`);
      return;
    }

    setIsSaving(true);
    let emailSent = false;
    try {
      if (editingCoach) {
        const newTier = formData.tier as 'basic' | 'basic_plus' | 'advanced' | 'partner';
        const tierChanged = editingCoach.tier !== newTier;
        const updatePayload: Record<string, any> = {
          tier: newTier,
          max_respondents: formData.respondentLimit
        };
        if (tierChanged) {
          updatePayload.ai_analysis_access = ['advanced', 'partner'].includes(newTier);
        }

        await supabase
          .from('coaches')
          .update(updatePayload)
          .eq('id', editingCoach.id);

        await supabase
          .from('users')
          .update({ full_name: formData.name })
          .eq('id', editingCoach.userId);
      } else {
        const assessmentCode = 'HPOS' + crypto.randomUUID().slice(0, 6).toUpperCase();
        const { data: result, error: fnError } = await supabase.functions.invoke('create-coach', {
          body: {
            email: formData.email,
            password: formData.password,
            fullName: formData.name,
            tier: formData.tier,
            assessmentCode,
            partnerId,
            maxRespondents: formData.respondentLimit,
            locale: "tr",
          },
        });

        if (fnError) {
          let errMsg = 'Koc hesabi olusturulamadi';
          try {
            const errBody = await (fnError as any).context?.json?.();
            if (errBody?.error) errMsg = errBody.error;
          } catch {
            if (result?.error) errMsg = result.error;
          }
          throw new Error(errMsg);
        }

        emailSent = result?.emailSent === true;
      }

      const wasAdding = !editingCoach;
      await loadCoaches(partnerId);
      setShowAddModal(false);
      setEditingCoach(null);
      setModalError(null);
      setFormData({ name: '', email: '', password: '', tier: 'basic', respondentLimit: 25 });

      if (wasAdding) {
        setSuccessMessage(
          emailSent
            ? 'Koc hesabi basariyla olusturuldu! Giris bilgileri koca e-posta ile gonderildi.'
            : 'Koc hesabi basariyla olusturuldu. E-posta gonderilemedi -- lutfen giris bilgilerini manuel olarak paylasin.'
        );
      }
    } catch (err: any) {
      console.error('Error saving coach:', err);
      setModalError(err.message || 'Koc kaydedilemedi');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (coachId: string) => {
    if (!confirm('Bu kocu silmek istediginizden emin misiniz?')) return;
    if (!partnerId) return;

    try {
      const { error: deleteError } = await supabase
        .from('coaches')
        .delete()
        .eq('id', coachId);

      if (deleteError) throw deleteError;

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error deleting coach:', err);
      setError('Koc silinemedi');
    }
  };

  const toggleTeamsAccess = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ teams_access_enabled: !coach.teamsAccessEnabled })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling teams access:', err);
      setError('Takim erisimi guncellenemedi');
    }
  };

  const toggleCRMAccess = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ crm_enabled: !coach.crmEnabled })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling CRM access:', err);
      setError('CRM erisimi guncellenemedi');
    }
  };

  const toggleAIAnalysis = async (coach: Coach) => {
    if (!partnerId) return;
    try {
      await supabase
        .from('coaches')
        .update({ ai_analysis_access: !coach.aiAnalysisAccess })
        .eq('id', coach.id);

      await loadCoaches(partnerId);
    } catch (err) {
      console.error('Error toggling AI analysis access:', err);
      setError('AI analiz erisimi guncellenemedi');
    }
  };

  const resendCredentials = async (coach: Coach) => {
    if (!confirm(`${coach.name} adli kocun sifresi sifirlanacak ve yeni giris bilgileri e-posta ile gonderilecek. Devam edilsin mi?`)) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'resend-credentials-coach',
            recipientEmail: coach.email,
            recipientName: coach.name,
            userId: coach.userId,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'E-posta gonderilemedi');
      setSuccessMessage(`Giris bilgileri ${coach.email} adresine yeniden gonderildi`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Bilgiler yeniden gonderilemedi');
    }
  };

  const openEditModal = (coach: Coach) => {
    setEditingCoach(coach);
    setFormData({
      name: coach.name,
      email: coach.email,
      password: '',
      tier: coach.tier,
      respondentLimit: coach.respondentLimit
    });
    setShowAddModal(true);
  };

  const filteredCoaches = coaches.filter(coach =>
    coach.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coach.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tierBadgeColors: Record<string, string> = {
    basic: 'bg-cream-200 text-chocolate-700',
    basic_plus: 'bg-sage-100 text-sage-700',
    advanced: 'bg-coral-100 text-coral-700',
    partner: 'bg-warm-100 text-warm-700'
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Koc yonetimi yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">Koc Yonetimi</h1>
                <p className="text-sm text-chocolate-500">{coaches.length} / {coachLimit} koc</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCoach(null);
                setError(null);
                setModalError(null);
                setFormData({ name: '', email: '', password: '', tier: 'basic', respondentLimit: 25 });
                setShowAddModal(true);
              }}
              disabled={coaches.length >= coachLimit}
              className="freud-btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              Koc Ekle
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Koc ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Koc</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">E-posta</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Seviye</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Katilimci Limiti</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Takimlar</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">CRM</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">AI Analiz</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Olusturulma</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Islemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredCoaches.map((coach) => (
                  <tr key={coach.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-chocolate-900">{coach.name}</span>
                      {coach.userId === user?.id && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warm-100 text-warm-700 uppercase">Siz</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4" />
                        {coach.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs capitalize ${tierBadgeColors[coach.tier]}`}>
                        {coach.tier === 'basic' ? 'temel' : coach.tier === 'basic_plus' ? 'temel plus' : coach.tier === 'advanced' ? 'ileri' : 'partner'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Users className="w-4 h-4" />
                        {coach.respondentLimit}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTeamsAccess(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.teamsAccessEnabled ? 'bg-sage-500' : 'bg-cream-300'
                        }`}
                        title={coach.teamsAccessEnabled ? 'Takimlar etkin' : 'Takimlar devre disi'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          coach.teamsAccessEnabled ? 'translate-x-4.5' : 'translate-x-1'
                        }`} style={{ transform: `translateX(${coach.teamsAccessEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleCRMAccess(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.crmEnabled ? 'bg-blue-500' : 'bg-cream-300'
                        }`}
                        title={coach.crmEnabled ? 'CRM etkin' : 'CRM devre disi'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} style={{ transform: `translateX(${coach.crmEnabled ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleAIAnalysis(coach); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          coach.aiAnalysisAccess ? 'bg-amber-500' : 'bg-cream-300'
                        }`}
                        title={coach.aiAnalysisAccess ? 'AI Analiz etkin' : 'AI Analiz devre disi'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} style={{ transform: `translateX(${coach.aiAnalysisAccess ? '18px' : '2px'})` }} />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4" />
                        {coach.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => resendCredentials(coach)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Bilgileri Yeniden Gonder"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(coach)}
                          className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(coach.id)}
                          className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCoaches.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              Henuz koc yok
            </div>
          )}
        </div>
      </main>

      <Toast
        isVisible={!!successMessage}
        message={successMessage || ''}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-6">
              {editingCoach ? 'Kocu Duzenle' : 'Koc Ekle'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Ad
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="freud-input"
                />
              </div>
              {!editingCoach && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">
                      E-posta
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="freud-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">
                      Sifre
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Minimum 8 karakter"
                      className="freud-input"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Seviye
                </label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="freud-input"
                >
                  <option value="basic">Temel</option>
                  <option value="basic_plus">Temel Plus</option>
                  <option value="advanced">Ileri</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Katilimci Limiti
                </label>
                <input
                  type="number"
                  value={formData.respondentLimit}
                  onChange={(e) => setFormData({ ...formData, respondentLimit: parseInt(e.target.value) })}
                  min="1"
                  required
                  className="freud-input"
                />
              </div>
              {modalError && (
                <div className="bg-coral-100 border border-coral-200 text-coral-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {modalError}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCoach(null);
                    setModalError(null);
                  }}
                  className="freud-btn-secondary flex-1"
                >
                  Iptal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="freud-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Kaydediliyor...' : editingCoach ? 'Degisiklikleri Kaydet' : 'Koc Ekle'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
